import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import jsPDF from 'jspdf';
import { TCHA_LOGO_BASE64 } from './logo';
import { SERVICE_OPTIONS } from './services';

// Initialize Supabase with environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

// Strip characters that aren't safe in filenames and collapse whitespace to underscores
function sanitizeForFilename(str) {
  return (str || '')
    .trim()
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '_');
}

export default function TCHAInvoiceApp({ onBack, backLabel = '← Dashboard', editInvoiceId }) {
  const isEditMode = Boolean(editInvoiceId);

  const [formData, setFormData] = useState({
    billToName: '',
    billToAddress: '',
    serviceCategory: 'Training',
    paymentEmail: 'hiral@hiralsoni.com',
    invoiceDate: new Date().toISOString().split('T')[0],
    lineItems: [{ description: '', quantity: 1, amount: 0, isCustom: false }],
    amountPaid: 0,
  });

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [dbInvoiceId, setDbInvoiceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');

  const generateInvoiceNumber = useCallback(async () => {
    try {
      const date = new Date();
      const monthYear = date.getFullYear().toString().slice(2) + String(date.getMonth() + 1).padStart(2, '0');

      // Get count of invoices for this month to generate sequential number
      const { data, error } = await supabase
        .from('TCHA_invoices')
        .select('invoice_number', { count: 'exact' })
        .like('invoice_number', `${monthYear}-%`);

      if (error) throw error;

      const count = data ? data.length + 1 : 1;
      const newNumber = `${monthYear}-${String(count).padStart(3, '0')}`;
      setInvoiceNumber(newNumber);
    } catch (error) {
      console.error('Error generating invoice number:', error);
      showMessage('Could not connect to database', 'error');
    }
  }, []);

  const loadInvoiceForEdit = useCallback(async (id) => {
    setInitialLoading(true);
    setLoadError('');
    try {
      const { data: invoice, error: invErr } = await supabase
        .from('TCHA_invoices')
        .select('*')
        .eq('id', id)
        .single();

      if (invErr) throw invErr;
      if (!invoice) throw new Error('Invoice not found');

      const { data: items, error: itemsErr } = await supabase
        .from('TCHA_invoice_line_items')
        .select('*')
        .eq('invoice_id', id)
        .order('created_at', { ascending: true });

      if (itemsErr) throw itemsErr;

      setInvoiceNumber(invoice.invoice_number || '');
      setDbInvoiceId(invoice.id);
      setFormData({
        billToName: invoice.bill_to_name || '',
        billToAddress: invoice.bill_to_address || '',
        serviceCategory: invoice.service_category || '',
        paymentEmail: invoice.payment_email || '',
        invoiceDate: invoice.invoice_date || new Date().toISOString().split('T')[0],
        lineItems: (items && items.length > 0)
          ? items.map(i => ({
              description: i.description,
              quantity: parseFloat(i.quantity) || 1,
              amount: parseFloat(i.amount) || 0,
              isCustom: !!i.description && !SERVICE_OPTIONS.includes(i.description),
            }))
          : [{ description: '', quantity: 1, amount: 0, isCustom: false }],
        amountPaid: parseFloat(invoice.amount_paid) || 0,
      });
    } catch (error) {
      console.error('Error loading invoice:', error);
      setLoadError('Could not load this invoice. It may have been deleted.');
    } finally {
      setInitialLoading(false);
    }
  }, []);

  // Generate invoice number on mount (create mode) or load existing invoice (edit mode)
  useEffect(() => {
    if (editInvoiceId) {
      loadInvoiceForEdit(editInvoiceId);
    } else {
      generateInvoiceNumber();
    }
  }, [editInvoiceId, loadInvoiceForEdit, generateInvoiceNumber]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amountPaid' ? parseFloat(value) || 0 : value
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: field === 'quantity' || field === 'amount' ? parseFloat(value) || 0 : value
    };
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems
    }));
  };

  // Handles the service dropdown: picking a listed service fills the description directly;
  // picking "Other" switches that row into free-text mode.
  const handleServiceSelectChange = (index, value) => {
    setFormData(prev => {
      const newLineItems = [...prev.lineItems];
      newLineItems[index] = value === 'Other'
        ? { ...newLineItems[index], isCustom: true, description: '' }
        : { ...newLineItems[index], isCustom: false, description: value };
      return { ...prev, lineItems: newLineItems };
    });
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, amount: 0, isCustom: false }]
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return formData.lineItems.reduce((sum, item) => sum + (item.quantity * item.amount), 0);
  };

  const calculateBalance = () => {
    const total = calculateTotal();
    return total - formData.amountPaid;
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    if (type === 'success') {
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!formData.billToName.trim()) {
      showMessage('Please enter client name', 'error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const total = calculateTotal();
      const balance = calculateBalance();

      if (isEditMode) {
        // Update existing invoice
        const { error: updateError } = await supabase
          .from('TCHA_invoices')
          .update({
            invoice_number: invoiceNumber,
            invoice_date: formData.invoiceDate,
            bill_to_name: formData.billToName,
            bill_to_address: formData.billToAddress,
            service_category: formData.serviceCategory,
            payment_email: formData.paymentEmail,
            total_amount: total,
            amount_paid: formData.amountPaid,
            balance: balance,
            updated_at: new Date().toISOString(),
          })
          .eq('id', dbInvoiceId);

        if (updateError) throw updateError;

        // Replace line items: remove old ones, insert the corrected set
        const { error: deleteError } = await supabase
          .from('TCHA_invoice_line_items')
          .delete()
          .eq('invoice_id', dbInvoiceId);

        if (deleteError) throw deleteError;

        const lineItemsToInsert = formData.lineItems
          .filter(item => item.description)
          .map(item => ({
            invoice_id: dbInvoiceId,
            description: item.description,
            quantity: item.quantity,
            amount: item.amount
          }));

        if (lineItemsToInsert.length > 0) {
          const { error: lineItemError } = await supabase
            .from('TCHA_invoice_line_items')
            .insert(lineItemsToInsert);

          if (lineItemError) throw lineItemError;
        }

        await generatePDF(total, balance);
        showMessage('✓ Invoice updated successfully!', 'success');
      } else {
        // Create new invoice
        const { data: invoiceData, error: invoiceError } = await supabase
          .from('TCHA_invoices')
          .insert([{
            invoice_number: invoiceNumber,
            invoice_date: formData.invoiceDate,
            bill_to_name: formData.billToName,
            bill_to_address: formData.billToAddress,
            service_category: formData.serviceCategory,
            payment_email: formData.paymentEmail,
            total_amount: total,
            amount_paid: formData.amountPaid,
            balance: balance
          }])
          .select();

        if (invoiceError) throw invoiceError;

        const newInvoiceId = invoiceData[0].id;

        const lineItemsToInsert = formData.lineItems
          .filter(item => item.description)
          .map(item => ({
            invoice_id: newInvoiceId,
            description: item.description,
            quantity: item.quantity,
            amount: item.amount
          }));

        if (lineItemsToInsert.length > 0) {
          const { error: lineItemError } = await supabase
            .from('TCHA_invoice_line_items')
            .insert(lineItemsToInsert);

          if (lineItemError) throw lineItemError;
        }

        await generatePDF(total, balance);
        showMessage('✓ Invoice created and saved successfully!', 'success');

        // Reset form and generate new invoice number for the next invoice
        setTimeout(() => {
          generateInvoiceNumber();
          setFormData({
            billToName: '',
            billToAddress: '',
            serviceCategory: 'Training',
            paymentEmail: 'hiral@hiralsoni.com',
            invoiceDate: new Date().toISOString().split('T')[0],
            lineItems: [{ description: '', quantity: 1, amount: 0, isCustom: false }],
            amountPaid: 0,
          });
        }, 1000);
      }

    } catch (error) {
      showMessage(`✗ Error: ${error.message}`, 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (total, balance) => {
    const pdf = new jsPDF('p', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Colors
    const textColor = [0, 0, 0];

    // Top margin
    let yPosition = 15;

    // Logo
    try {
      pdf.addImage(TCHA_LOGO_BASE64, 'PNG', 15, yPosition, 28, 28);
    } catch (e) {
      console.error('Error adding logo to PDF:', e);
    }

    // INVOICE title
    pdf.setFontSize(28);
    pdf.setTextColor(...textColor);
    pdf.text('INVOICE', pageWidth - 50, yPosition + 12);

    yPosition += 35;

    // Company name
    pdf.setFontSize(16);
    pdf.setTextColor(...textColor);
    pdf.text('The Canadian Health Academy', 20, yPosition);

    yPosition += 12;

    // Invoice metadata (right aligned)
    pdf.setFontSize(11);
    const metaX = pageWidth - 60;
    pdf.text(`DATE: ${new Date(formData.invoiceDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, metaX, yPosition);
    yPosition += 6;
    pdf.text(`INVOICE #: ${invoiceNumber}`, metaX, yPosition);
    yPosition += 6;
    pdf.text(`FOR: ${formData.serviceCategory}`, metaX, yPosition);

    yPosition += 12;

    // Bill To
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    pdf.text('Bill To:', 20, yPosition);
    yPosition += 6;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    pdf.text(formData.billToName, 20, yPosition);
    if (formData.billToAddress) {
      yPosition += 5;
      pdf.text(formData.billToAddress, 20, yPosition);
    }

    yPosition += 15;

    // Table header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(20, yPosition, pageWidth - 40, 7, 'F');
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(10);
    pdf.text('DESCRIPTION', 25, yPosition + 5);
    pdf.text('QTY', pageWidth - 75, yPosition + 5);
    pdf.text('AMOUNT', pageWidth - 35, yPosition + 5);

    yPosition += 7;

    // Line items
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(9);
    formData.lineItems.forEach(item => {
      if (item.description) {
        pdf.text(item.description, 25, yPosition + 4);
        pdf.text(item.quantity.toString(), pageWidth - 75, yPosition + 4);
        pdf.text(`$${(item.amount).toFixed(2)}`, pageWidth - 35, yPosition + 4);
        yPosition += 6;
      }
    });

    yPosition += 5;

    // Totals section
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(10);
    const totalX = pageWidth - 60;
    pdf.text('TOTAL', totalX - 20, yPosition);
    pdf.text(`$${total.toFixed(2)}`, pageWidth - 35, yPosition);

    yPosition += 6;
    pdf.setFont(undefined, 'normal');
    pdf.text('Amount Paid', totalX - 20, yPosition);
    pdf.text(`$${formData.amountPaid.toFixed(2)}`, pageWidth - 35, yPosition);

    yPosition += 6;
    pdf.text('Balance', totalX - 20, yPosition);
    pdf.text(`$${balance.toFixed(2)}`, pageWidth - 35, yPosition);

    yPosition += 12;

    // Footer
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Email transfer accepted at ${formData.paymentEmail}`, 20, pageHeight - 20);

    pdf.setFontSize(10);
    pdf.setTextColor(...textColor);
    pdf.text('THANK YOU FOR YOUR BUSINESS!', pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Download PDF
    const safeClientName = sanitizeForFilename(formData.billToName) || 'Client';
    pdf.save(`TCHA_Invoice_${invoiceNumber}_${safeClientName}.pdf`);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)', padding: '2rem' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        {onBack && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={onBack}
              style={{
                padding: '0.5rem 0.875rem',
                background: 'white',
                color: '#c8102e',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
              }}
            >
              {backLabel}
            </button>
          </div>
        )}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>
            {isEditMode ? 'Edit Invoice' : 'TCHA Invoice Generator'}
          </h1>
          <p style={{ color: '#6b7280' }}>
            {isEditMode ? `Correcting invoice ${invoiceNumber || ''}` : 'Create and store professional invoices'}
          </p>
        </div>

        {isEditMode && initialLoading && (
          <div style={{ background: 'white', borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)', padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            Loading invoice...
          </div>
        )}

        {isEditMode && !initialLoading && loadError && (
          <div style={{ background: 'white', borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)', padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: '#991b1b', marginBottom: '1rem' }}>{loadError}</p>
            {onBack && (
              <button onClick={onBack} style={{ padding: '0.5rem 1rem', background: '#c8102e', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: 600, cursor: 'pointer' }}>
                {backLabel}
              </button>
            )}
          </div>
        )}

        {(!isEditMode || (!initialLoading && !loadError)) && (
        <>
        {/* Main Form */}
        <div style={{ background: 'white', borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)', padding: '2rem', marginBottom: '1.5rem' }}>
          {/* Message Display */}
          {message && (
            <div style={{
              padding: '1rem',
              borderRadius: '0.375rem',
              marginBottom: '1rem',
              backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: message.type === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`
            }}>
              {message.text}
            </div>
          )}

          {/* Invoice Number Display */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Invoice Number</label>
              <input type="text" value={invoiceNumber} disabled style={{ width: '100%', padding: '0.5rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: 'bold' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Invoice Date</label>
              <input type="date" name="invoiceDate" value={formData.invoiceDate} onChange={handleFormChange} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
            </div>
          </div>

          {/* Bill To Section */}
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>Bill To</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Client Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="text" name="billToName" value={formData.billToName} onChange={handleFormChange} placeholder="Enter client name" style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Address</label>
              <input type="text" name="billToAddress" value={formData.billToAddress} onChange={handleFormChange} placeholder="Enter client address" style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Service Category</label>
                <input type="text" name="serviceCategory" value={formData.serviceCategory} onChange={handleFormChange} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Payment Email</label>
                <input type="email" name="paymentEmail" value={formData.paymentEmail} onChange={handleFormChange} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>Services</h3>
            <div>
              {formData.lineItems.map((item, index) => (
                <div key={index} style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 80px', gap: '0.75rem', alignItems: 'end' }}>
                    <select
                      value={item.isCustom ? 'Other' : (item.description || '')}
                      onChange={(e) => handleServiceSelectChange(index, e.target.value)}
                      style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontFamily: 'inherit', fontSize: '0.875rem', background: 'white', color: item.description ? '#111827' : '#6b7280' }}
                    >
                      <option value="" disabled>Select a service...</option>
                      {SERVICE_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                      <option value="Other">Other (type custom)</option>
                    </select>
                    <input type="number" value={item.quantity} onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)} min="1" style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
                    <input type="number" value={item.amount} onChange={(e) => handleLineItemChange(index, 'amount', e.target.value)} min="0" step="0.01" style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
                    <button onClick={() => removeLineItem(index)} style={{ padding: '0.5rem', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem' }}>Remove</button>
                  </div>
                  {item.isCustom && (
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      placeholder="Enter custom service name"
                      style={{ marginTop: '0.5rem', width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxSizing: 'border-box' }}
                    />
                  )}
                </div>
              ))}
            </div>
            <button onClick={addLineItem} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#e5e7eb', color: '#1f2937', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600' }}>+ Add Service</button>
          </div>

          {/* Payment Section */}
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>Payment</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Total</label>
                <input type="text" value={`$${calculateTotal().toFixed(2)}`} disabled style={{ width: '100%', padding: '0.5rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: 'bold' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Amount Paid</label>
                <input type="number" name="amountPaid" value={formData.amountPaid} onChange={handleFormChange} min="0" step="0.01" style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Balance</label>
                <input type="text" value={`$${calculateBalance().toFixed(2)}`} disabled style={{ width: '100%', padding: '0.5rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: 'bold' }} />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button onClick={handleGenerateInvoice} disabled={loading || !formData.billToName} style={{ width: '100%', padding: '0.75rem', background: loading || !formData.billToName ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: 'bold', fontSize: '1rem', cursor: loading || !formData.billToName ? 'not-allowed' : 'pointer', marginTop: '1.5rem' }}>
            {loading
              ? (isEditMode ? '⏳ Updating Invoice...' : '⏳ Generating Invoice...')
              : (isEditMode ? 'Update Invoice' : 'Generate & Save Invoice')}
          </button>
        </div>

        {/* Instructions */}
        <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '0.5rem', padding: '1.5rem', color: '#1e3a8a' }}>
          <h4 style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>How it works:</h4>
          <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
            {isEditMode ? (
              <>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ Correct any details below</li>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ Add, edit, or remove services as needed</li>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ Click "Update Invoice" to save your changes</li>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ A corrected PDF downloads automatically</li>
              </>
            ) : (
              <>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ Fill in the invoice details above</li>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ Add multiple services as needed</li>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ Click "Generate & Save Invoice"</li>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ PDF downloads automatically</li>
                <li style={{ fontSize: '0.875rem', marginBottom: '0.375rem' }}>✓ Data is saved to Supabase for reporting</li>
              </>
            )}
          </ul>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
